import { useContext, useEffect, Fragment, useState, useCallback } from 'react'
import styled from '@emotion/styled'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { Checkbox, ListItem, ListItemText, List, Collapse } from '@mui/material'
import { func, object } from 'prop-types'
import { CvsButton } from '@components'
import { targetContext, fieldSetContext } from '@contexts'
import { t } from '@lingui/core/macro'

const Padding = styled.div`
  padding: 12px;
  height: 100%;
  display: flex;
  flex-direction: column;
`

const BottomButton = styled(CvsButton)`
  width: 100%;
`

export const TargetSelector = ({ onDone = () => {}, field = {}, fieldSet = {} }) => {
  const { byId, fetchTargets } = useContext(targetContext)
  const { byId: fieldSetById } = useContext(fieldSetContext)
  const [openTarget, setOpenTarget] = useState('')
  const [checked, setChecked] = useState({})
  const [disabled, setDisabled] = useState({})
  const fieldId = field.id
  const fieldSetId = fieldSet.id

  useEffect(() => {
    // when it first loads calculated the fields already selected
    const usedTargetFields = fieldSet.fields?.reduce((memo, item) => {
      if (item.id !== fieldId) {
        return (
          item.target_fields?.reduce(
            (acc, tf) => ({
              ...acc,
              [tf.id]: true,
            }),
            memo,
          ) || memo
        )
      }
      return memo
    }, {})
    setDisabled(usedTargetFields)
  }, [])

  useEffect(() => {
    fetchTargets().then(() => {
      const sel = field?.target_fields?.reduce(
        (acc, targetField) => ({
          ...acc,
          [targetField.target.id]: acc[targetField.target.id]
            ? [...acc[targetField.target.id], targetField.id]
            : [targetField.id],
        }),
        {},
      )
      setChecked(sel || {})
    })
  }, [fetchTargets])

  const handleSave = useCallback(() => {
    const target_fields =
      Object.keys(checked)?.reduce(
        (acc, key) => [
          ...acc,
          ...checked[key].map(targetFieldId =>
            byId[key].target_fields.find(targetField => targetField.id === targetFieldId),
          ),
        ],
        [],
      ) || []

    onDone({
      ...field,
      target_fields,
    })
  }, [fieldId, fieldSetById, byId, checked, fieldSetId])

  const handleTargetClick = useCallback(
    (e, id) => {
      e.stopPropagation()
      e.preventDefault()
      setOpenTarget(prev => (prev === id ? '' : id))
    },
    [byId],
  )

  const handleTargetFieldClick = useCallback(
    (targetFieldId, targetId) => {
      setChecked(prev => {
        const target = prev[targetId]
        return {
          ...prev,
          [targetId]:
            target && target.includes(targetFieldId)
              ? [...target].filter(id => id !== targetFieldId)
              : [...(target || []), targetFieldId],
        }
      })
    },
    [setChecked],
  )

  const allFieldsSelected = useCallback(
    targetId =>
      checked[targetId] ? byId[targetId].target_fields.length === checked[targetId].length : false,
    [checked, byId],
  )

  const isIndeterminate = useCallback(
    targetId =>
      checked[targetId]
        ? checked[targetId].length > 0 &&
          checked[targetId].length < byId[targetId].target_fields.length
        : false,
    [checked, byId],
  )

  return (
    <Padding>
      <div style={{ flex: 1 }}>
        <List dense>
          {Object.keys(byId).map(id => (
            <Fragment key={id}>
              <ListItem
                onClick={() => setOpenTarget(prev => (prev === id ? '' : id))}
                sx={{ cursor: 'pointer' }}
              >
                <Checkbox
                  checked={allFieldsSelected(id)}
                  indeterminate={isIndeterminate(id)}
                  onClick={e => handleTargetClick(e, id)}
                  color="secondary"
                />
                <ListItemText
                  primary={byId[id].name}
                  sx={{ '> span': { color: 'rgba(0,0,0,0.87)' } }}
                />
                {openTarget === id ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse in={openTarget === id} sx={{ paddingLeft: '20px' }}>
                <List dense>
                  {byId[id].target_fields?.map(field => (
                    <ListItem key={field.id} sx={{ cursor: 'pointer' }}>
                      <Checkbox
                        checked={checked[id] ? checked[id].includes(field.id) : false}
                        onClick={() => handleTargetFieldClick(field.id, id)}
                        disabled={disabled[field.id]}
                        color="secondary"
                      />
                      <ListItemText
                        primary={field.name}
                        sx={{
                          '> span': {
                            color: disabled[field.id] ? 'rgba(0,0,0,0.26)' : 'rgba(0,0,0,0.87)',
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Fragment>
          ))}
        </List>
      </div>
      <div style={{ flexShrink: 1 }}>
        <BottomButton color="secondary" variant="contained" onClick={handleSave}>
          {t`Done`}
        </BottomButton>
      </div>
    </Padding>
  )
}

TargetSelector.propTypes = {
  close: func,
  onDone: func.isRequired,
  field: object.isRequired,
  fieldSet: object.isRequired,
}
